import { useState } from 'react'
import { TrendingUp } from 'lucide-react'
import { progressService } from '../../services/progressService'
import { TaskService, type Task } from '../../services/taskService'

/**
 * Progress, from the task page (owner 2026-09-05: "no easy way to record
 * progress for a task from this view"). A bar that says where it stands and
 * a small logger: slide or tap to a number, add a word, save. Session-counted
 * tasks get "+1 session". Records land in History through the progress API.
 */
interface Props {
  task: Task
  onChanged: (task: Task) => void
  /** Called after a record is saved, so History refetches. */
  onLogged?: () => void
  headingClass: string
}

const QUICK = [25, 50, 75, 100]

export function TaskProgress({ task, onChanged, onLogged, headingClass }: Props) {
  const svc = new TaskService()
  const sessions = (task.sessions_target ?? 0) > 0
  const current = sessions
    ? Math.min(100, Math.round(((task.sessions_done || 0) / (task.sessions_target as number)) * 100))
    : Math.min(100, Math.max(0, Math.round(task.progress || 0)))
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState(current)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const isOpen = task.status !== 'completed' && task.status !== 'cancelled'

  const log = async () => {
    setBusy(true); setErr(null)
    try {
      await progressService.updateProgress('task', task.id, {
        progress_value: value,
        notes: note.trim() || undefined,
        progress_type: value >= 100 ? 'completion' : 'update',
      })
      setNote('')
      setOpen(false)
      onChanged(await svc.getTask(task.id))
      onLogged?.()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save that')
    } finally {
      setBusy(false)
    }
  }

  const addSession = async () => {
    setBusy(true); setErr(null)
    try {
      await svc.updateTask(task.id, { sessions_done: (task.sessions_done || 0) + 1 })
      onChanged(await svc.getTask(task.id))
      onLogged?.()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save that')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-testid="task-progress">
      <div className="flex items-center justify-between gap-2">
        <h3 className={headingClass}><TrendingUp className="h-3.5 w-3.5 text-brand" /> Progress</h3>
        {isOpen && !open && (
          // A section's verb is the quiet link every section on the page uses
          // (About's Edit, Steps' Break into steps); the page's one filled
          // button is the next move in the header.
          <button type="button" onClick={() => { setValue(current); setOpen(true) }} className="text-app-meta font-medium text-brand hover:underline" data-testid="task-progress-log">
            Log progress
          </button>
        )}
      </div>
      <div className="mt-1.5 flex items-center gap-2 text-sm text-primary">
        <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-soft" aria-hidden="true">
          <span className="block h-full bg-brand" style={{ width: `${current}%` }} />
        </span>
        <span className="shrink-0 tabular-nums" data-testid="task-progress-value">
          {sessions ? `${task.sessions_done || 0} of ${task.sessions_target} sessions` : `${current}%`}
        </span>
      </div>
      {isOpen && sessions && !open && (
        <button type="button" onClick={() => void addSession()} disabled={busy} className="mt-1.5 text-app-meta font-medium text-brand hover:underline disabled:opacity-60" data-testid="task-progress-session">
          +1 session
        </button>
      )}
      {open && (
        <div className="mt-2 space-y-2" data-testid="task-progress-form">
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={value}
              onChange={(e) => setValue(Number(e.target.value))}
              aria-label="Progress"
              className="flex-1 accent-brand"
            />
            <span className="w-10 text-right text-sm tabular-nums text-primary">{value}%</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {QUICK.map((q) => (
              <button key={q} type="button" onClick={() => setValue(q)} aria-pressed={value === q}
                      className={`rounded-full border px-2.5 py-1 text-app-micro font-medium ${value === q ? 'border-brand bg-brand text-on-brand' : 'border-soft bg-surface text-secondary hover:border-brand'}`}>
                {q === 100 ? 'Done' : `${q}%`}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void log() } }}
            placeholder="What moved? (optional)"
            aria-label="Progress note"
            className="w-full rounded-md border border-soft bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30"
            data-testid="task-progress-note"
          />
          {err && <p className="text-xs text-bad" role="alert">{err}</p>}
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => void log()} disabled={busy} className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-on-brand hover:opacity-90 disabled:opacity-60" data-testid="task-progress-save">
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => setOpen(false)} disabled={busy} className="px-2 py-1.5 text-sm text-muted hover:text-secondary">Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default TaskProgress
